import React, { useState, useEffect, useCallback } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Select,
    InputNumber,
    DatePicker,
    Input,
    Tag,
    Collapse,
    Tooltip,
    Spin,
    message,
} from "antd";
import {
    PlusOutlined,
    InboxOutlined,
    ExportOutlined,
    ImportOutlined,
    WarningOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useSupplies } from "../context/SuppliesContext";
import { fetchWarehouseMovements } from "../utils/fetch";

const { Panel } = Collapse;

const WarehouseTab = () => {
    const { warehouseStock, addIncoming, setStockDirect, deleteStockEntry } = useSupplies();
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // Инлайн редактирование количества
    const [editingKey, setEditingKey] = useState(null); // materialName редактируемой строки
    const [editingValue, setEditingValue] = useState(null);
    const [savingKey, setSavingKey] = useState(null); // ключ строки, которая сохраняется

    // Журнал операций
    const [movements, setMovements] = useState([]);
    const [movementsLoading, setMovementsLoading] = useState(false);
    const [movementsTotal, setMovementsTotal] = useState(0);
    const [movementsPage, setMovementsPage] = useState(1);

    const loadMovements = useCallback(async (page = 1) => {
        setMovementsLoading(true);
        try {
            const data = await fetchWarehouseMovements(page, 20);
            setMovements(data.movements || []);
            setMovementsTotal(data.total || 0);
            setMovementsPage(page);
        } catch (e) {
            console.error(e);
        } finally {
            setMovementsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadMovements(1);
    }, [loadMovements]);

    // Удаление позиции со склада
    const handleDelete = (record) => {
        Modal.confirm({
            title: `Удалить позицию ${record.materialName}?`,
            content: `На складе числится ${record.inStock || 0} шт. Удалить можно только позицию с нулевым остатком. Вы уверены?`,
            okText: "Да, удалить",
            cancelText: "Отмена",
            okType: "danger",
            onOk: async () => {
                try {
                    await deleteStockEntry(record._id);
                    loadMovements(1);
                } catch (e) {
                    console.error(e);
                }
            }
        });
    };

    // Сохранение инлайн-редактирования
    const handleSaveInStock = useCallback(async (materialName, oldVal) => {
        // Если пользователь не изменил значение — выходим без запроса
        if (editingValue === null || editingValue === (oldVal || 0)) {
            setEditingKey(null);
            setEditingValue(null);
            return;
        }

        setEditingKey(null);
        setSavingKey(materialName);
        try {
            await setStockDirect(materialName, editingValue);
            loadMovements(1); // обновить журнал
        } catch (e) {
            console.error(e);
        } finally {
            setSavingKey(null);
            setEditingValue(null);
        }
    }, [editingValue, setStockDirect, loadMovements]);

    // Инлайн редактирование сроков доставки
    const [editingDaysKey, setEditingDaysKey] = useState(null);
    const [editingDaysValue, setEditingDaysValue] = useState(null);
    const [savingDaysKey, setSavingDaysKey] = useState(null);

    const { updateMinMaxDays } = useSupplies();

    const handleSaveDays = useCallback(async (record) => {
        const oldDays = record.deliveryDays || 2;
        if (editingDaysValue === null || editingDaysValue === oldDays) {
            setEditingDaysKey(null);
            setEditingDaysValue(null);
            return;
        }

        setEditingDaysKey(null);
        setSavingDaysKey(record.materialName);
        try {
            await updateMinMaxDays(record.materialName, record.minStock, record.maxStock, editingDaysValue);
            message.success(`Срок доставки для "${record.materialName}" обновлён`);
        } catch (e) {
            console.error(e);
        } finally {
            setSavingDaysKey(null);
            setEditingDaysValue(null);
        }
    }, [editingDaysValue, updateMinMaxDays]);

    // Обработка прихода — через контекст (он обновит warehouseStock и planData мгновенно)
    const handleIncoming = async (values) => {
        setSubmitting(true);
        try {
            await addIncoming(
                values.materialName,
                values.quantity,
                values.date ? values.date.toISOString() : undefined,
                values.comment || ""
            );
            setModalVisible(false);
            form.resetFields();
            // Обновить журнал операций
            loadMovements(1);
        } catch (e) {
            // Ошибка уже показана в контексте
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    // Колонки таблицы остатков
    const stockColumns = [
        {
            title: "№",
            key: "num",
            width: 45,
            align: "center",
            render: (_, __, idx) => idx + 1,
        },
        {
            title: "Наименование",
            dataIndex: "materialName",
            key: "materialName",
            width: 250,
            sorter: (a, b) => a.materialName.localeCompare(b.materialName, "ru"),
        },
        {
            title: "Ед. изм.",
            dataIndex: "unit",
            key: "unit",
            width: 70,
            align: "center",
        },
        {
            title: "На складе",
            dataIndex: "inStock",
            key: "inStock",
            width: 120,
            align: "center",
            sorter: (a, b) => (a.inStock || 0) - (b.inStock || 0),
            render: (val, record) => {
                const isLow = record.minStock > 0 && (val || 0) < record.minStock;
                const isEditing = editingKey === record.materialName;
                const isSaving = savingKey === record.materialName;

                if (isSaving) {
                    return <Spin size="small" />;
                }

                if (isEditing) {
                    return (
                        <InputNumber
                            autoFocus
                            min={0}
                            size="small"
                            value={editingValue}
                            onChange={(v) => setEditingValue(v)}
                            onPressEnter={() => handleSaveInStock(record.materialName, val)}
                            onBlur={() => handleSaveInStock(record.materialName, val)}
                            style={{ width: 80 }}
                        />
                    );
                }

                return (
                    <Tooltip title="Нажмите для редактирования">
                        <span
                            style={{
                                fontWeight: 600,
                                color: isLow ? "#ff4d4f" : "#52c41a",
                                cursor: "pointer",
                                borderBottom: "1px dashed currentColor",
                                paddingBottom: 1,
                            }}
                            onClick={() => {
                                setEditingKey(record.materialName);
                                setEditingValue(val || 0);
                            }}
                        >
                            {val || 0}
                            {isLow && (
                                <Tooltip title={`Ниже минимального запаса (${record.minStock})`}>
                                    <WarningOutlined style={{ marginLeft: 4, color: "#ff4d4f" }} />
                                </Tooltip>
                            )}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "Min",
            dataIndex: "minStock",
            key: "minStock",
            width: 70,
            align: "center",
            render: (val) => val || "—",
        },
        {
            title: "Max",
            dataIndex: "maxStock",
            key: "maxStock",
            width: 70,
            align: "center",
            render: (val) => val || "—",
        },
        {
            title: "Категория",
            dataIndex: "category",
            key: "category",
            width: 120,
            align: "center",
            filters: [
                { text: "Вшитые", value: "IN_BUILT" },
                { text: "Дополнительные", value: "ADDITIONAL" },
            ],
            onFilter: (value, record) => record.category === value,
            render: (val) =>
                val === "IN_BUILT" ? (
                    <Tag color="blue">Вшитые</Tag>
                ) : (
                    <Tag>Доп.</Tag>
                ),
        },
        {
            title: "Срок дост.",
            dataIndex: "deliveryDays",
            key: "deliveryDays",
            width: 80,
            align: "center",
            render: (val, record) => {
                const days = val || 2;
                const isEditing = editingDaysKey === record.materialName;
                const isSaving = savingDaysKey === record.materialName;

                if (isSaving) {
                    return <Spin size="small" />;
                }

                if (isEditing) {
                    return (
                        <InputNumber
                            autoFocus
                            min={1}
                            size="small"
                            value={editingDaysValue}
                            onChange={(v) => setEditingDaysValue(v)}
                            onPressEnter={() => handleSaveDays(record)}
                            onBlur={() => handleSaveDays(record)}
                            style={{ width: 60 }}
                        />
                    );
                }

                return (
                    <Tooltip title="Нажмите для редактирования">
                        <span
                            onClick={() => {
                                setEditingDaysKey(record.materialName);
                                setEditingDaysValue(days);
                            }}
                            style={{
                                color: days >= 30 ? "#ff4d4f" : "#52c41a",
                                fontWeight: days >= 30 ? 700 : 600,
                                cursor: "pointer",
                                borderBottom: "1px dashed currentColor",
                                paddingBottom: 1,
                            }}
                        >
                            {days} дн.
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "",
            key: "action",
            width: 45,
            align: "center",
            render: (_, record) => (
                <Tooltip title="Удалить позицию">
                    <Button 
                        type="text" 
                        danger 
                        icon={<DeleteOutlined />} 
                        onClick={() => handleDelete(record)}
                        disabled={(record.inStock || 0) > 0} 
                    />
                </Tooltip>
            ),
        },
    ];

    // Колонки журнала
    const movementColumns = [
        {
            title: "Дата",
            dataIndex: "date",
            key: "date",
            width: 130,
            render: (val) =>
                val ? moment(val).format("DD.MM.YYYY HH:mm") : "—",
        },
        {
            title: "Операция",
            dataIndex: "type",
            key: "type",
            width: 110,
            align: "center",
            render: (val) =>
                val === "INCOMING" ? (
                    <Tag icon={<ImportOutlined />} color="green">
                        Приход
                    </Tag>
                ) : (
                    <Tag icon={<ExportOutlined />} color="red">
                        Списание
                    </Tag>
                ),
        },
        {
            title: "Позиция",
            dataIndex: "materialName",
            key: "materialName",
            width: 220,
        },
        {
            title: "Кол-во",
            dataIndex: "quantity",
            key: "quantity",
            width: 80,
            align: "center",
            render: (val, record) => (
                <span
                    style={{
                        fontWeight: 600,
                        color: record.type === "INCOMING" ? "#52c41a" : "#ff4d4f",
                    }}
                >
                    {record.type === "INCOMING" ? "+" : "−"}
                    {val}
                </span>
            ),
        },
        {
            title: "Кто",
            key: "user",
            width: 140,
            render: (_, record) => {
                const user = record.responsibleUserId;
                if (user && typeof user === "object") {
                    return `${user.name || ""} ${user.surname || ""}`.trim() || "—";
                }
                return "Система";
            },
        },
        {
            title: "Комментарий",
            dataIndex: "comment",
            key: "comment",
            width: 200,
            render: (val) => val || "—",
        },
    ];

    // Опции для Select — из текущих позиций на складе
    const materialOptions = (warehouseStock || [])
        .sort((a, b) => a.materialName.localeCompare(b.materialName, "ru"))
        .map((item) => ({
            label: item.materialName,
            value: item.materialName,
        }));

    return (
        <div style={{ marginTop: 16 }}>
            {/* Кнопка «Добавить приход» */}
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setModalVisible(true)}
                    size="large"
                >
                    Добавить приход
                </Button>
            </div>

            {/* Таблица остатков */}
            <Table
                size="small"
                bordered
                columns={stockColumns}
                dataSource={(warehouseStock || []).map((item) => ({
                    ...item,
                    key: item._id,
                }))}
                pagination={false}
                scroll={{ y: 500 }}
                rowClassName={(record) => {
                    if (
                        record.minStock > 0 &&
                        (record.inStock || 0) < record.minStock
                    ) {
                        return "warehouse-row-warning";
                    }
                    return "";
                }}
                style={{ marginBottom: 24 }}
            />

            {/* Журнал операций */}
            <Collapse defaultActiveKey={[]}>
                <Panel
                    header={
                        <span>
                            <InboxOutlined style={{ marginRight: 8 }} />
                            Журнал операций ({movementsTotal})
                        </span>
                    }
                    key="movements"
                >
                    <Table
                        size="small"
                        bordered
                        columns={movementColumns}
                        dataSource={(movements || []).map((m, i) => ({
                            ...m,
                            key: m._id || i,
                        }))}
                        loading={movementsLoading}
                        pagination={{
                            current: movementsPage,
                            pageSize: 20,
                            total: movementsTotal,
                            onChange: (page) => loadMovements(page),
                            showSizeChanger: false,
                        }}
                    />
                </Panel>
            </Collapse>

            {/* Модалка прихода */}
            <Modal
                title="Добавить приход товара на склад"
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleIncoming}
                    initialValues={{ date: moment() }}
                >
                    <Form.Item
                        name="materialName"
                        label="Позиция"
                        rules={[{ required: true, message: "Выберите позицию" }]}
                    >
                        <Select
                            showSearch
                            placeholder="Выберите комплектующее"
                            options={materialOptions}
                            filterOption={(input, option) =>
                                (option?.label ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name="quantity"
                        label="Количество"
                        rules={[
                            { required: true, message: "Введите количество" },
                            {
                                type: "number",
                                min: 1,
                                message: "Должно быть больше 0",
                            },
                        ]}
                    >
                        <InputNumber
                            style={{ width: "100%" }}
                            min={1}
                            placeholder="Количество"
                        />
                    </Form.Item>

                    <Form.Item name="date" label="Дата поступления">
                        <DatePicker
                            style={{ width: "100%" }}
                            format="DD.MM.YYYY"
                        />
                    </Form.Item>

                    <Form.Item name="comment" label="Комментарий">
                        <Input.TextArea
                            rows={2}
                            placeholder="Необязательный комментарий"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={submitting}
                            block
                        >
                            Добавить на склад
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* CSS для подсветки строк */}
            <style>{`
                .warehouse-row-warning {
                    background-color: #fff7e6 !important;
                }
                .warehouse-row-warning:hover > td {
                    background-color: #ffe7ba !important;
                }
            `}</style>
        </div>
    );
};

export default WarehouseTab;
